import type { Incoterms } from 'api';
import { PortAutocomplete } from 'form';
import { useContext } from 'react';
import { Box, Tooltip } from 'ui';
import { InfoOutlined } from 'ui/icons';

import { PurchaseOrderFormContext } from '~/orders/contexts/PurchaseOrderFormContext';

const INCOTERM_CONFIG = {
  CFR: { portOfLoadingRequired: false, showPortOfDischarge: true, showPortOfLoading: true },
  CIF: { portOfLoadingRequired: false, showPortOfDischarge: true, showPortOfLoading: true },
  EXW: { portOfLoadingRequired: false, showPortOfDischarge: false, showPortOfLoading: true },
  FAS: { portOfLoadingRequired: true, showPortOfDischarge: false, showPortOfLoading: true },
  FCA: { portOfLoadingRequired: true, showPortOfDischarge: false, showPortOfLoading: true },
  FOB: { portOfLoadingRequired: true, showPortOfDischarge: false, showPortOfLoading: true },
} as const;

type IncotermConfigKey = keyof typeof INCOTERM_CONFIG;

interface Props {
  incoterm?: Incoterms;
}

export const IncotermSpecificFields = ({ incoterm }: Props) => {
  const { defaultValues } = useContext(PurchaseOrderFormContext);
  if (!incoterm || !(incoterm in INCOTERM_CONFIG)) {
    return null;
  }

  const config = INCOTERM_CONFIG[incoterm as IncotermConfigKey];

  return (
    <>
      {config.showPortOfLoading ? (
        <Box display="flex" gap={1} alignItems="center">
          <PortAutocomplete
            name="port_of_loading_id"
            label="Port of loading"
            required={config.portOfLoadingRequired}
            defaultValue={defaultValues?.port_of_loading_id}
          />
          <Tooltip title="Port of departure, at which containers are loaded on the vessel.">
            <InfoOutlined />
          </Tooltip>
        </Box>
      ) : null}
      {config.showPortOfDischarge ? (
        <Box display="flex" gap={1} alignItems="center">
          <PortAutocomplete
            name="port_of_discharge_id"
            label="Port of discharge"
            required
            defaultValue={defaultValues?.port_of_discharge_id}
          />
          <Tooltip title="Port of arrival, at which containers are unloaded from the vessel.">
            <InfoOutlined />
          </Tooltip>
        </Box>
      ) : null}
    </>
  );
};
